import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useSearchParams } from "react-router-dom";
import { usePageState } from "@/contexts/NavigationContext";
import api from "@/lib/api";
import { fmtL, fmtDate, apiErr } from "@/lib/format";
import { fileToCompressedDataUrl } from "@/lib/image";
import { toast } from "sonner";
import { useAuth, useFechaOperacion } from "@/contexts/AuthContext";
import StatementExportModal from "@/components/StatementExportModal";
import { FilePdfIcon, XIcon, PaperclipIcon, TrashIcon, ReceiptIcon, BuildingsIcon, PlusIcon, MinusIcon, LightningIcon, CheckIcon, WarningIcon } from "@phosphor-icons/react";
import { useModalForm } from "@/lib/modalForm";
import BotonCerrar from "@/components/BotonCerrar";
import { ReciboImpresionModal } from "@/components/ReciboPago";
import { useLiveSearch, SearchBox } from "@/components/LiveSearch";
import CarteraCliente from "@/pages/_payments/CarteraCliente";

/**
 * Tab "Cuentas por Cobrar" del módulo 6.
 * PAG-05 estilo agrupado por cliente · CLI-01 multi-cobro · PAG-01 banco→cuenta · PAG-02 comprobante · PAG-03 recibo PDF
 */
export default function ReceivablesTab() {
  // El RTC consulta su cartera; el cobro lo registra en "Cobros" para aprobación del Administrador General
  const { user: usr } = useAuth();
  const soloConsulta = Boolean(usr?.effective?.alcance_regional);
  const [items, setItems] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = usePageState("q", "");
  // Modal multi-cobro
  const [payingClient, setPayingClient] = useState(null);   // {cliente_id, cliente_nombre, facturas: [], preseleccion?}
  const [imprimir, setImprimir] = useState(null);            // recibo recién registrado → vista de impresión
  // «Recibir pago» desde la factura: /cxc?cobrar=<factura_id>&cliente=<cliente_id> abre ESTE mismo recibo oficial
  const [params, setParams] = useSearchParams();
  const pedido = useRef(params.get("cobrar") ? { factura: params.get("cobrar"), cliente: params.get("cliente") } : null);

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, { data: ba }] = await Promise.all([
        api.get("/receivables", { params: { incluir_nd: true } }),   // facturas + notas de débito con saldo propio
        usr?.effective?.alcance_regional ? Promise.resolve({ data: [] }) : api.get("/treasury/accounts").catch(() => ({ data: [] })),
      ]);
      setItems(data);
      setBankAccounts(ba.filter((a) => a.activo !== false));
    } catch (e) { toast.error(apiErr(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Agrupar por cliente (PAG-05)
  const grouped = useMemo(() => {
    const map = new Map();
    for (const f of items) {
      const key = f.cliente_id;
      if (!map.has(key)) {
        map.set(key, {
          cliente_id: f.cliente_id,
          cliente_nombre: f.cliente_nombre,
          cliente_codigo: f.cliente_codigo || "",
          facturas: [],
          total: 0,
          vencido: 0,
        });
      }
      const g = map.get(key);
      g.facturas.push(f);
      g.total += f.saldo_pendiente || 0;
      if (f.vencida) g.vencido += f.saldo_pendiente || 0;
    }
    return Array.from(map.values()).sort((a, b) => b.total - a.total);
  }, [items]);

  useEffect(() => {
    const ped = pedido.current;
    if (!ped || loading) return;
    pedido.current = null;
    setParams((prev) => { const n = new URLSearchParams(prev); n.delete("cobrar"); n.delete("cliente"); return n; }, { replace: true });
    const g = grouped.find((x) => x.facturas.some((f) => f.id === ped.factura && (f.documento_tipo || "factura") === "factura"));
    if (!g) { toast.error("La factura ya no tiene saldo pendiente (o no está en tu cartera): revisa los recibos del cliente."); return; }
    if (soloConsulta) { toast.error("Tu cobro se registra en «Cobros» para autorización del Administrador General."); return; }
    setQ("");
    setPayingClient({ ...g, preseleccion: ped.factura });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, grouped]);

  const totalPendiente = items.reduce((s, i) => s + i.saldo_pendiente, 0);
  const vencidas = items.filter((i) => i.vencida);

  // Filtro por estado (a nivel de factura) y búsqueda al escribir: cliente, RTN, código, factura, proyecto o estado
  const [filtroEstado, setFiltroEstado] = useState("todas");
  const FILTROS = [["todas", "Todas"], ["vencidas", "Vencidas"], ["por_vencer", "Por vencer"], ["nc", "Con saldo en NC"], ["tramite", "En trámite"]];
  const pasaEstado = (f) => filtroEstado === "todas" || (filtroEstado === "vencidas" && f.vencida) || (filtroEstado === "por_vencer" && !f.vencida)
    || (filtroEstado === "nc" && Number(f.saldo_nc || 0) > 0.004) || (filtroEstado === "tramite" && f.bloqueo_saldo);
  const porEstado = useMemo(() => grouped.map((g) => {
    const facturas = g.facturas.filter(pasaEstado);
    return facturas.length === g.facturas.length ? g : { ...g, facturas, total: facturas.reduce((s2, f) => s2 + (f.saldo_pendiente || 0), 0),
      vencido: facturas.filter((f) => f.vencida).reduce((s2, f) => s2 + (f.saldo_pendiente || 0), 0) };
  }).filter((g) => g.facturas.length), [grouped, filtroEstado]); // eslint-disable-line react-hooks/exhaustive-deps
  const busqueda = useLiveSearch(porEstado, (g) => [g.cliente_nombre, g.cliente_codigo, g.cliente_rtn,
    ...(g.facturas || []).flatMap((f) => [f.numero, f.proyecto_codigo, f.proyecto_nombre, f.subcuenta_codigo, f.vencida ? "vencida" : "por vencer",
      Number(f.saldo_nc || 0) > 0 ? "nc" : "", f.bloqueo_saldo ? "en tramite" : ""])], { estado: [q, setQ] });
  const filteredGrouped = busqueda.resultados;
  const saldoNc = items.reduce((s2, i) => s2 + Math.min(Number(i.saldo_pendiente || 0), Number(i.saldo_nc || 0)), 0);

  return (
    <>
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 mb-4">
        <Kpi label="Cobranza ordinaria" v={fmtL(totalPendiente - saldoNc)} accent="green" />
        <Kpi label="En subcuenta NC" v={fmtL(saldoNc)} accent="" />
        <Kpi label="Clientes con saldo" v={grouped.length} accent="" />
        <Kpi label="Facturas vencidas" v={vencidas.length} accent="red" />
        <Kpi label="Saldo vencido" v={fmtL(vencidas.reduce((s, i) => s + i.saldo_pendiente, 0))} accent="yellow" />
      </div>

      <div className="mb-4 space-y-2 sticky top-16 z-20 bg-app/95 backdrop-blur py-2 -mx-1 px-1" data-testid="receivables-search-wrap">
        <SearchBox busqueda={busqueda} placeholder="Buscar cliente, factura, proyecto o estado…" testid="receivables-search" label="Buscar en cuentas por cobrar" />
        <div className="flex gap-1.5 overflow-x-auto pb-1" role="group" aria-label="Filtrar por estado">
          {FILTROS.map(([k, l]) => (
            <button key={k} type="button" onClick={() => setFiltroEstado(k)} aria-pressed={filtroEstado === k} data-testid={`cxc-filtro-${k}`}
              className={`min-h-[40px] px-3 rounded-full border text-xs whitespace-nowrap ${filtroEstado === k ? "bg-agro-green/15 border-agro-green/50 text-agro-green font-semibold" : "border-border text-text-secondary"}`}>{l}</button>
          ))}
        </div>
      </div>

      <div className="space-y-3">
        {loading ? (
          <div className="bg-card border border-border/60 rounded-lg p-8 text-center text-text-muted">Cargando…</div>
        ) : filteredGrouped.length === 0 ? (
          <div className="bg-card border border-border/60 rounded-lg p-12 text-center text-text-muted">
            {busqueda.activa ? `Sin coincidencias para «${busqueda.texto}».` : "Sin saldos pendientes"}
          </div>
        ) : filteredGrouped.map((g) => (
          <CarteraCliente key={g.cliente_id} g={g} onCambio={load}
            onPay={soloConsulta ? null : (f) => { const base = grouped.find((x) => x.cliente_id === g.cliente_id) || g; setPayingClient(f ? { ...base, preseleccion: f.id } : base); }} />
        ))}
      </div>

      {payingClient && (
        <MultiPaymentModal
          group={payingClient}
          bankAccounts={bankAccounts}
          onClose={() => setPayingClient(null)}
          onSaved={(pago) => { setPayingClient(null); load(); if (pago?.id) setImprimir(pago); }}
        />
      )}
      {/* El recibo ya está guardado: la vista solo lo consulta (reimprimir o cerrar no duplica el cobro) */}
      {imprimir && <ReciboImpresionModal pagoId={imprimir.id} numero={imprimir.numero_recibo} onClose={() => setImprimir(null)} />}
    </>
  );
}

const todayYmd = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** «Recibir pago». Con `edicion` (un recibo existente, solo Administrador) el mismo formulario se abre precargado y
 *  guarda la corrección sobre ESE recibo (mismo número; el servidor recalcula saldos, banco, asiento y GRATIVA). */
export function MultiPaymentModal({ group, bankAccounts, onClose, onSaved, edicion = null }) {
  const ed = edicion || null;
  const mpEd = ed?.moneda_pago || {};
  const { user, hasPermission } = useAuth();
  // 7.1 — cambiar el tipo de venta (directa / RTC) de una factura antes de confirmar requiere permiso
  const canChangeTipo = ["admin", "accountant"].includes(user?.role) || hasPermission?.("payments.authorize") || hasPermission?.("payments.change_tipo_venta");
  const fechaOp = useFechaOperacion();
  const [fechaPago, setFechaPago] = useState(ed ? String(ed.fecha || "").slice(0, 10) : (fechaOp.fija ? fechaOp.hoy : todayYmd()));   // 6.1 fecha real del pago
  const [proyectosCliente, setProyectosCliente] = useState([]);
  const [montoTotal, setMontoTotal] = useState(() => {
    if (ed) return String(Number(ed.monto_total ?? ed.monto ?? 0).toFixed(2));
    const f = group.preseleccion && group.facturas.find((x) => x.id === group.preseleccion);
    return f ? String(Number(f.saldo_pendiente || 0).toFixed(2)) : "";
  });
  const [metodo, setMetodo] = useState(ed?.metodo || "efectivo");
  // Moneda en que se recibe el pago: siempre se aplica a la cuenta en lempiras
  const [moneda, setMoneda] = useState(mpEd.moneda || "HNL");
  const [montoUsd, setMontoUsd] = useState(mpEd.monto_moneda ? String(mpEd.monto_moneda) : "");          // USD recibidos (pagos que no son en efectivo)
  const [tipoCambio, setTipoCambio] = useState(mpEd.tipo_cambio ? String(mpEd.tipo_cambio) : "");      // lempiras por cada dólar
  const [efectivo, setEfectivo] = useState(mpEd.efectivo_recibido && (mpEd.moneda === "USD" || mpEd.cambio_hnl > 0) ? String(mpEd.efectivo_recibido) : "");          // efectivo entregado por el cliente, en `moneda`
  const cuentaEd = ed ? (bankAccounts.find((c) => c.id === (ed.cuenta_destino?.id || ed.cuenta_id)) || null) : null;
  const [bancoSel, setBancoSel] = useState(cuentaEd && ed?.metodo !== "efectivo" ? (cuentaEd.banco || "") : "");        // PAG-01: nombre del banco seleccionado
  const [cuentaId, setCuentaId] = useState(cuentaEd ? cuentaEd.id : "");        // PAG-01: cuenta bancaria filtrada
  const [referencia, setReferencia] = useState(ed?.referencia || "");
  const [notas, setNotas] = useState(ed?.notas || "");
  const [comprobantes, setComprobantes] = useState([]); // PAG-02: imágenes/PDFs
  // Un mismo cobro reenviado (doble clic / reintento) devuelve el recibo ya registrado: no se duplica
  const [idemKey] = useState(() => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `${Date.now()}-${Math.random()}`));
  const [critAbierto, setCritAbierto] = useState(null);   // factura con el editor de criterios abierto

  // Facturas ordenadas por fecha de vencimiento ASC (más vencidas primero)
  // Fallback: si no hay fecha_vencimiento, usar fecha de emisión.
  const facturasOrdenadas = useMemo(() =>
    [...group.facturas].sort((a, b) => {
      // «Recibir pago» desde una factura: esa factura primero (el reparto automático empieza por ella)
      if (group.preseleccion && (a.id === group.preseleccion) !== (b.id === group.preseleccion)) return a.id === group.preseleccion ? -1 : 1;
      const da = a.fecha_vencimiento || a.fecha || "9999-12-31";
      const db = b.fecha_vencimiento || b.fecha || "9999-12-31";
      return String(da).localeCompare(String(db));
    })
  , [group.facturas, group.preseleccion]);

  const [allocations, setAllocations] = useState(
    facturasOrdenadas.map((f) => ({
      factura_id: f.id, factura_numero: f.documento_tipo === "nota_debito" ? `ND ${f.numero}` : f.numero,
      documento_tipo: f.documento_tipo || "factura", saldo: f.saldo_pendiente,
      fecha_vencimiento: f.fecha_vencimiento || f.fecha || "",
      vencida: !!f.vencida,
      memo: f.memo || "",
      // se rellena automáticamente al escribir montoTotal; al editar, lo que el recibo aplicó a ese documento
      monto: ed ? Math.round((ed.allocations?.length ? ed.allocations : [{ factura_id: ed.factura_id, monto: Number(ed.monto || 0) - Number(ed.monto_excedente || 0) }])
        .filter((x) => (x.nota_debito_id || x.factura_id) === f.id).reduce((t, x) => t + Number(x.monto || 0), 0) * 100) / 100 : 0,
      proyecto_id: f.proyecto_id || "",
      tipo_venta: f.tipo_venta || "directa",
      tipo_venta_original: f.tipo_venta || "directa",
      vendedor: f.vendedor || "",
      subcuenta_codigo: f.subcuenta_codigo || "",
      // factura histórica: criterios GRATIVA (proveedor / categoría / nivel / RTC) guardados o por capturar
      requiere_criterios: Boolean(f.requiere_criterios) && (f.documento_tipo || "factura") === "factura",
      criterios_guardados: f.criterios_comision || null,
      criterios: null,
    }))
  );
  const setAllocCriterios = (idx, c) => setAllocations((prev) => prev.map((x, k) => (k === idx ? { ...x, criterios: c } : x)));
  const sumaCrit = (a) => Math.round((a.criterios?.lineas || []).reduce((t, l) => t + (Number(l.monto) || 0), 0) * 100) / 100;
  const [saving, setSaving] = useState(false);
  const [autoMode, setAutoMode] = useState(!ed); // reparto automático activo por defecto (al editar se respeta la aplicación del recibo)

  useEffect(() => {
    api.get(`/clients/${group.cliente_id}/projects`).then(({ data }) => setProyectosCliente(data || [])).catch(() => {});
  }, [group.cliente_id]);

  // Reparto automático: rellena desde la factura más vencida en cascada.
  // NOTA: `restante` DEBE inicializarse DENTRO del updater de setAllocations
  // porque React.StrictMode invoca el updater dos veces y compartir la variable
  // por closure la agotaba en la 2ª pasada (dejando todos los montos en 0).
  const autoAllocate = (amount) => {
    const total = Math.max(0, Number(amount) || 0);
    setAllocations((prev) => {
      let restante = total;
      return prev.map((a) => {
        const cuota = Math.min(restante, a.saldo);
        restante -= cuota;
        return { ...a, monto: cuota > 0 ? Number(cuota.toFixed(2)) : 0 };
      });
    });
  };

  // Cuando cambie el monto entregado y autoMode esté activo → redistribuir
  useEffect(() => {
    if (autoMode) autoAllocate(montoTotal);
  }, [montoTotal, autoMode]);

  // PAG-01: lista única de bancos disponibles
  const bancos = useMemo(() => Array.from(new Set(bankAccounts.map((a) => a.banco).filter(Boolean))).sort(), [bankAccounts]);
  const cuentasBanco = useMemo(() =>
    bankAccounts.filter((a) => (bancoSel ? a.banco === bancoSel : false))
  , [bankAccounts, bancoSel]);
  const cuentasCaja = useMemo(() => bankAccounts.filter((a) => a.tipo === "caja"), [bankAccounts]);

  // Moneda y cambio: equivalente en HNL = USD × tipo de cambio; cambio (HNL) = efectivo en HNL − monto aplicado
  const esUsd = moneda === "USD";
  const esEfectivo = metodo === "efectivo";
  const tc = esUsd ? Number(tipoCambio) || 0 : 1;
  const usdDerivado = esUsd && !esEfectivo;               // el monto en L sale de USD × tipo de cambio
  useEffect(() => {
    if (usdDerivado) setMontoTotal(Number(montoUsd) > 0 && tc > 0 ? r2(Number(montoUsd) * tc).toFixed(2) : "");
  }, [usdDerivado, montoUsd, tc]);
  const aplicadoHnl = r2(montoTotal);
  const efectivoIndicado = efectivo !== "" && Number(efectivo) > 0;
  const efectivoHnl = esEfectivo ? (efectivoIndicado ? r2(Number(efectivo) * tc) : (esUsd ? 0 : aplicadoHnl)) : 0;
  const cambioHnl = r2(efectivoHnl - aplicadoHnl);
  const faltaEfectivo = esEfectivo && aplicadoHnl > 0 && (esUsd ? tc > 0 && efectivoIndicado : efectivoIndicado) && cambioHnl < -0.004;

  const sumaAlloc = allocations.reduce((s, a) => s + (Number(a.monto) || 0), 0);
  const excedente = Math.max(0, Number(montoTotal) - sumaAlloc);
  const exceso = sumaAlloc > Number(montoTotal) + 0.01;

  const setAllocMonto = (idx, v) => {
    // Al editar manualmente, desactivar auto-mode
    setAutoMode(false);
    const n = [...allocations];
    n[idx].monto = v;
    setAllocations(n);
  };
  const setAllocTipo = (idx, v) => {
    const n = [...allocations];
    n[idx] = { ...n[idx], tipo_venta: v };
    setAllocations(n);
  };
  const setAllocProyecto = (idx, v) => {
    const n = [...allocations];
    n[idx].proyecto_id = v;
    setAllocations(n);
  };

  const onAddComprobante = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { toast.error("El archivo excede 5 MB"); return; }
    try {
      const dataUrl = await fileToCompressedDataUrl(file, 1600, 0.85);
      setComprobantes([...comprobantes, { url: dataUrl, descripcion: file.name }]);
      toast.success("Comprobante adjuntado");
    } catch { toast.error("No se pudo leer el archivo"); }
    e.target.value = "";
  };

  const submit = async (e) => {
    e.preventDefault();
    if (exceso) { toast.error("La suma de aplicaciones excede el monto entregado"); return; }
    if (esUsd && !(tc > 0)) { toast.error("El tipo de cambio (lempiras por dólar) debe ser mayor que cero"); return; }
    if (usdDerivado && !(Number(montoUsd) > 0)) { toast.error("Indica el monto recibido en dólares"); return; }
    if (esEfectivo && esUsd && !efectivoIndicado) { toast.error("Indica el efectivo recibido en dólares"); return; }
    if (Number(montoTotal) <= 0) { toast.error("Monto total debe ser > 0"); return; }
    if (faltaEfectivo) { toast.error(`El efectivo recibido no cubre el monto a aplicar: faltan ${fmtL(-cambioHnl)}`); return; }
    const allocs = allocations.filter((a) => Number(a.monto) > 0);
    if (allocs.length === 0) { toast.error("Aplica al menos a una factura"); return; }
    if (metodo !== "efectivo" && !cuentaId) {
      toast.error("Selecciona la cuenta bancaria donde ingresó el cobro");
      return;
    }
    for (const a of allocs) {
      if (!a.criterios || !(a.criterios.lineas || []).length) continue;
      if (a.criterios.lineas.some((l) => !(l.proveedor_nombre || "").trim() || !l.categoria || !l.nivel || !(Number(l.monto) > 0))) {
        toast.error(`Factura ${a.factura_numero}: completa proveedor, categoría, nivel e importe de cada criterio`); return;
      }
      if (Math.abs(sumaCrit(a) - Number(a.monto)) > 0.01) {
        toast.error(`Factura ${a.factura_numero}: el desglose suma ${fmtL(sumaCrit(a))} y el pago aplicado es ${fmtL(Number(a.monto))}`); return;
      }
    }
    setSaving(true);
    try {
      const payload = {
        cliente_id: group.cliente_id,
        monto_total: Number(montoTotal),
        metodo,
        referencia,
        banco: bancoSel,
        notas,
        cuenta_id: cuentaId || null,
        fecha: (fechaOp.fija ? fechaOp.hoy : fechaPago) || null,
        allocations: allocs.map((a) => (a.documento_tipo === "nota_debito"
          ? { nota_debito_id: a.factura_id, monto: Number(a.monto) }   // cobro a nota de débito (saldo propio)
          : {
            factura_id: a.factura_id,
            proyecto_id: a.proyecto_id || null,
            monto: Number(a.monto),
            ...(a.tipo_venta !== a.tipo_venta_original ? { tipo_venta: a.tipo_venta } : {}),
            ...(a.criterios && (a.criterios.lineas || []).length ? {
              criterios: { rtc_id: a.criterios.rtc_id || null, lineas: a.criterios.lineas.map((l) => ({ ...l, monto: Number(l.monto) })) },
            } : {}),
          })),
        ...(ed ? {} : { idempotency_key: idemKey }),
        moneda,
        ...(esUsd ? { tipo_cambio: tc } : {}),
        ...(usdDerivado ? { monto_moneda: Number(montoUsd) } : {}),
        ...(esEfectivo && efectivoIndicado ? { efectivo_recibido: Number(efectivo) } : {}),
      };
      const { data: pago } = ed ? await api.put(`/payments/${ed.id}`, payload) : await api.post("/payments/multi", payload);
      // PAG-02: subir comprobantes si hay
      for (const c of comprobantes) {
        try {
          await api.post(`/payments/${pago.id}/attachments`, c);
        } catch { /* tolerable */ }
      }
      toast.success(ed
        ? `Recibo ${pago.numero_recibo || ""} actualizado · se recalcularon saldos, banco y contabilidad`
        : `Recibo ${pago.numero_recibo || ""} registrado · ${allocs.length} factura(s)${excedente > 0.01 ? ` · saldo a favor L. ${excedente.toFixed(2)}` : ""}`);
      const cambio = pago.moneda_pago?.cambio_hnl;
      if (cambio > 0.004) toast.message(`Cambio a entregar al cliente: ${fmtL(cambio)}`, { duration: 12000 });
      if (pago.comision_estado === "pendiente") {
        const mot = [...new Set((pago.comision_pendientes || []).map((x) => `${x.factura_numero}: ${x.detalle}`))];
        toast.warning(`GRATIVA pendiente de definir (el cobro sí quedó registrado): ${mot.join(" · ")}`, { duration: 12000 });
      }
      onSaved(pago);
    } catch (e) { toast.error(apiErr(e)); }
    finally { setSaving(false); }
  };

  const mp = useModalForm({ value: { fechaPago, montoTotal, metodo, moneda, montoUsd, tipoCambio, efectivo, bancoSel, cuentaId, referencia, notas }, onClose: onClose, onSave: submit });
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" {...mp.overlayProps}>
      <div className="bg-card border border-border rounded-lg w-full max-w-4xl max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="multi-payment-modal">
        <div className="p-5 border-b border-border flex items-center justify-between sticky top-0 bg-card z-10">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-agro-yellow">
              {ed ? <span data-testid="editar-recibo-titulo">Editar recibo {ed.numero_recibo} · el número se conserva</span> : "Recibo oficial de pago"}
            </div>
            <h3 className="font-heading text-lg font-semibold">
              {group.cliente_codigo && <span className="font-mono text-sm text-agro-green mr-2" data-testid="cobro-cliente-codigo">{group.cliente_codigo}</span>}
              {group.cliente_nombre}
            </h3>
            <div className="text-xs text-text-muted">{group.facturas.length} facturas · Saldo total {fmtL(group.total)}</div>
            {group.preseleccion && (
              <div className="text-xs text-agro-green mt-0.5" data-testid="cobro-preseleccion">
                Factura seleccionada: {group.facturas.find((f) => f.id === group.preseleccion)?.numero} (puedes aplicar a otras del cliente)
              </div>
            )}
          </div>
          <button aria-label="Cerrar" onClick={mp.cerrar} className="text-text-muted hover:text-text-primary"><XIcon size={20} /></button>
        </div>

        <form onSubmit={mp.guardar} className="p-5 space-y-5">
          {/* Moneda en que se recibe el pago (se aplica a la cuenta en lempiras) */}
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-[10px] uppercase tracking-wider text-text-secondary font-medium">Moneda del pago</span>
            <div className="inline-flex rounded-md border border-border overflow-hidden" role="radiogroup" data-testid="multi-moneda">
              {[["HNL", "Lempiras (HNL)"], ["USD", "Dólares (USD)"]].map(([k, t]) => (
                <button key={k} type="button" role="radio" aria-checked={moneda === k}
                  onClick={() => { setMoneda(k); setEfectivo(""); setMontoUsd(""); if (k === "HNL") setTipoCambio(""); }}
                  data-testid={`multi-moneda-${k}`}
                  className={`px-3 py-1.5 text-xs font-semibold ${moneda === k ? "bg-agro-green text-black" : "bg-app text-text-secondary hover:text-text-primary"}`}>
                  {t}
                </button>
              ))}
            </div>
            {esUsd && <span className="text-[11px] text-text-muted">El pago se aplica a la cuenta en lempiras con el tipo de cambio indicado.</span>}
          </div>
          {esUsd && (
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3" data-testid="multi-usd">
              {!esEfectivo && (
                <div>
                  <Lbl>Monto recibido (USD) *</Lbl>
                  <input type="number" step="0.01" min="0" value={montoUsd} onChange={(e) => setMontoUsd(e.target.value)}
                    data-testid="multi-monto-usd" placeholder="0.00"
                    className="w-full bg-app border border-agro-green/40 rounded-md px-3 py-2 text-lg font-semibold" />
                </div>
              )}
              <div>
                <Lbl>Tipo de cambio (L por US$ 1) *</Lbl>
                <input type="number" step="0.0001" min="0" value={tipoCambio} onChange={(e) => setTipoCambio(e.target.value)}
                  data-testid="multi-tipo-cambio" placeholder="Ej: 26.00"
                  className={`w-full bg-app border rounded-md px-3 py-2 text-lg font-semibold ${tipoCambio !== "" && !(tc > 0) ? "border-red-500" : "border-agro-green/40"}`} />
                {tipoCambio !== "" && !(tc > 0) && <div className="text-[10px] text-red-400 mt-1">Debe ser mayor que cero</div>}
              </div>
              {!esEfectivo && (
                <div className="md:col-span-2 flex items-end">
                  <div className="text-sm bg-app border border-border rounded-md px-3 py-2 w-full" data-testid="multi-equivalente">
                    Equivalente en lempiras: <b className="text-agro-green">{Number(montoUsd) > 0 && tc > 0 ? fmtL(r2(Number(montoUsd) * tc)) : "—"}</b>
                    {Number(montoUsd) > 0 && tc > 0 && <span className="text-text-muted text-xs"> ({fmtUSD(montoUsd)} × {tc})</span>}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Datos del cobro */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
            <div>
              <Lbl>{usdDerivado ? "Equivalente a aplicar (L.)" : esEfectivo ? "Monto a aplicar a la cuenta (L.) *" : "Monto entregado (L.) *"}</Lbl>
              <input type="number" step="0.01" value={montoTotal} autoFocus={!esUsd}
                onChange={(e) => setMontoTotal(e.target.value)} readOnly={usdDerivado}
                title={usdDerivado ? "Se calcula: monto en USD × tipo de cambio" : undefined}
                placeholder={usdDerivado ? "USD × tipo de cambio" : `Ej: ${group.total.toFixed(2)} (pago total)`}
                data-testid="multi-monto-total"
                className={`w-full bg-app border border-agro-green/40 rounded-md px-3 py-2 text-lg font-semibold text-agro-green focus:border-agro-green focus:shadow-[0_0_0_2px_rgba(2,191,68,0.15)] ${usdDerivado ? "opacity-80 cursor-not-allowed" : ""}`} />
              {Number(montoTotal) > 0 && (() => {
                const facturasCubiertas = allocations.filter((a) => Number(a.monto) > 0);
                if (facturasCubiertas.length === 0) return null;
                const primera = facturasCubiertas[0];
                const restoCount = facturasCubiertas.length - 1;
                return (
                  <div className="text-[10px] text-agro-green mt-1 flex items-center gap-1 leading-tight" data-testid="magic-preview">
                    <LightningIcon size={12} className="inline align-[-2px]" aria-hidden="true" /> Se aplicará primero a <b className="font-mono">{primera.factura_numero}</b>
                    {restoCount > 0 && <span className="text-text-muted">+ {restoCount} factura{restoCount === 1 ? "" : "s"} en cascada</span>}
                    {excedente > 0.01 && <span className="text-agro-yellow">· sobrante L. {excedente.toFixed(2)}</span>}
                  </div>
                );
              })()}
            </div>
            <div>
              <Lbl>Método</Lbl>
              <select value={metodo} onChange={(e) => { setMetodo(e.target.value); setBancoSel(""); setCuentaId(""); }}
                data-testid="multi-metodo"
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm">
                <option value="efectivo">Efectivo</option>
                <option value="cheque">Cheque</option>
                <option value="transferencia">Transferencia</option>
                <option value="tarjeta">Tarjeta</option>
                <option value="deposito">Depósito</option>
              </select>
            </div>
            {metodo === "efectivo" ? (
              <div className="md:col-span-2">
                <Lbl>Caja</Lbl>
                <select value={cuentaId} onChange={(e) => setCuentaId(e.target.value)}
                  data-testid="multi-caja"
                  className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm">
                  <option value="">— Caja default —</option>
                  {cuentasCaja.map((c) => <option key={c.id} value={c.id}>{c.codigo} · {c.nombre}</option>)}
                </select>
              </div>
            ) : (
              <>
                <div>
                  <Lbl>Banco *</Lbl>
                  <select value={bancoSel} onChange={(e) => { setBancoSel(e.target.value); setCuentaId(""); }}
                    data-testid="multi-banco"
                    className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm">
                    <option value="">— Selecciona banco —</option>
                    {bancos.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </div>
                <div>
                  <Lbl>Cuenta bancaria *</Lbl>
                  <select value={cuentaId} onChange={(e) => setCuentaId(e.target.value)} disabled={!bancoSel}
                    data-testid="multi-cuenta"
                    className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm disabled:opacity-50">
                    <option value="">{bancoSel ? "— Selecciona cuenta —" : "Elige banco primero"}</option>
                    {cuentasBanco.map((c) => (
                      <option key={c.id} value={c.id}>{c.codigo} · {c.numero_cuenta || c.nombre}</option>
                    ))}
                  </select>
                </div>
              </>
            )}
            <div><Lbl>Fecha del pago *</Lbl>
              <input type="date" value={fechaOp.fija ? fechaOp.hoy : fechaPago} max={todayYmd()} onChange={(e) => setFechaPago(e.target.value)}
                data-testid="multi-fecha" required disabled={fechaOp.fija}
                title={fechaOp.fija ? "Tu rol registra solo con la fecha corriente de la empresa" : undefined}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" />
            </div>
            <div><Lbl>Referencia</Lbl>
              <input value={referencia} onChange={(e) => setReferencia(e.target.value)} placeholder="N° cheque / transferencia"
                data-testid="multi-ref"
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" />
            </div>
            <div className="md:col-span-2"><Lbl>Notas</Lbl>
              <input value={notas} onChange={(e) => setNotas(e.target.value)}
                className="w-full bg-app border border-border rounded-md px-3 py-2 text-sm" />
            </div>
          </div>

          {/* Efectivo: lo que entrega el cliente y el cambio (siempre en lempiras) */}
          {esEfectivo && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 items-start">
              <div>
                <Lbl>Efectivo recibido del cliente ({esUsd ? "USD" : "L."}){esUsd ? " *" : ""}</Lbl>
                <input type="number" step="0.01" min="0" value={efectivo} onChange={(e) => setEfectivo(e.target.value)}
                  data-testid="multi-efectivo" placeholder={esUsd ? "Ej: 100.00" : "Vacío = monto exacto"}
                  className="w-full bg-app border border-agro-green/40 rounded-md px-3 py-2 text-lg font-semibold" />
              </div>
              <div className="md:col-span-2 border border-border rounded-md p-3 text-sm space-y-1 bg-app" data-testid="resumen-efectivo">
                <div className="text-[10px] uppercase tracking-wider text-text-muted mb-1">Resumen antes de registrar</div>
                <div className="flex justify-between"><span>Monto que se aplicará a la cuenta</span><b data-testid="res-aplicado">{fmtL(aplicadoHnl)}</b></div>
                <div className="flex justify-between"><span>Efectivo entregado por el cliente</span>
                  <b data-testid="res-efectivo">{efectivoIndicado ? (esUsd ? fmtUSD(efectivo) : fmtL(Number(efectivo))) : esUsd ? "—" : `${fmtL(aplicadoHnl)} (exacto)`}</b></div>
                {esUsd && (
                  <div className="flex justify-between"><span>Equivalente en lempiras{tc > 0 ? ` (× ${tc})` : ""}</span>
                    <b data-testid="res-equivalente">{efectivoIndicado && tc > 0 ? fmtL(efectivoHnl) : "—"}</b></div>
                )}
                <div className={`flex justify-between border-t border-border pt-1 text-base ${faltaEfectivo ? "text-red-400" : "text-agro-green"}`}>
                  <span className="font-semibold">{faltaEfectivo ? "Falta efectivo" : "Cambio a entregar (en lempiras)"}</span>
                  <b data-testid={faltaEfectivo ? "res-faltante" : "res-cambio"}>
                    {(esUsd && !(efectivoIndicado && tc > 0)) ? "—" : fmtL(Math.abs(faltaEfectivo ? cambioHnl : Math.max(0, cambioHnl)))}
                  </b>
                </div>
              </div>
            </div>
          )}

          {/* PAG-02 — Comprobante */}
          <div>
            <Lbl>Comprobante de pago (PAG-02)</Lbl>
            <label className="flex items-center gap-2 px-3 py-2 border border-dashed border-border rounded-md text-sm text-text-secondary hover:border-agro-green/40 cursor-pointer w-fit"
              data-testid="multi-comprobante-label">
              <PaperclipIcon size={16} /> Subir imagen / PDF
              <input type="file" accept="image/*,application/pdf" onChange={onAddComprobante} className="hidden" data-testid="multi-comprobante-input" />
            </label>
            {comprobantes.length > 0 && (
              <div className="mt-2 space-y-1.5">
                {comprobantes.map((c, i) => (
                  <div key={i} className="flex items-center gap-3 bg-app border border-border/40 rounded p-2" data-testid={`comprobante-${i}`}>
                    {c.url.startsWith("data:image/") ? (
                      <img src={c.url} alt={c.descripcion} className="w-10 h-10 object-cover rounded" />
                    ) : (
                      <div className="w-10 h-10 bg-card flex items-center justify-center rounded text-text-muted">PDF</div>
                    )}
                    <div className="flex-1 text-xs truncate">{c.descripcion}</div>
                    <button aria-label="Eliminar" type="button" onClick={() => setComprobantes(comprobantes.filter((_, x) => x !== i))} className="text-text-muted hover:text-red-400 p-1"><TrashIcon size={14} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Aplicación a facturas */}
          <div>
            <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
              <Lbl>Aplicar a facturas / proyectos</Lbl>
              <div className="flex items-center gap-2 flex-wrap">
                {/* Quick-fill del saldo total del cliente */}
                <button
                  type="button"
                  onClick={() => { setAutoMode(true); setMontoTotal(group.total.toFixed(2)); }}
                  className="text-[10px] uppercase tracking-wider font-semibold rounded-md px-2.5 py-1.5 bg-agro-yellow/10 text-agro-yellow border border-agro-yellow/30 hover:bg-agro-yellow/20"
                  data-testid="quick-fill-total"
                  title={`Pago completo del saldo: L. ${group.total.toFixed(2)}`}
                >
                  = Saldo total ({fmtL(group.total)})
                </button>
                {group.vencido > 0 && (
                  <button
                    type="button"
                    onClick={() => { setAutoMode(true); setMontoTotal(group.vencido.toFixed(2)); }}
                    className="text-[10px] uppercase tracking-wider font-semibold rounded-md px-2.5 py-1.5 bg-red-500/10 text-red-400 border border-red-500/30 hover:bg-red-500/20"
                    data-testid="quick-fill-vencido"
                    title={`Solo lo vencido: L. ${group.vencido.toFixed(2)}`}
                  >
                    = Solo vencido ({fmtL(group.vencido)})
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => { setAutoMode(true); autoAllocate(montoTotal); }}
                  className={`text-xs font-semibold rounded-md px-3 py-1.5 flex items-center gap-1.5 border transition ${autoMode ? "bg-agro-green/15 text-agro-green border-agro-green/40" : "bg-app text-text-secondary border-border hover:border-agro-green/40 hover:text-agro-green"}`}
                  data-testid="auto-allocate-btn"
                  title="Aplica el monto a las facturas más vencidas primero, en cascada"
                >
                  <LightningIcon size={12} weight="fill" /> Auto-repartir
                </button>
              </div>
            </div>
            <div className="text-[11px] text-text-muted mb-2">
              {autoMode
                ? "Reparto automático activo — el sistema aplica primero a la factura más vencida y cascada al resto."
                : "Reparto manual — estás editando los montos por línea. Pulsa el botón para volver al automático."}
            </div>
            <div className="border border-border/60 rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-sidebar text-[10px] uppercase tracking-wider text-text-muted">
                  <tr className="text-left">
                    <th className="p-2 pl-4">Factura</th>
                    <th className="p-2">Vence</th>
                    <th className="p-2">Memo / Detalle</th>
                    <th className="p-2">Tipo de venta</th>
                    <th className="p-2 text-right">Saldo</th>
                    <th className="p-2 text-right">Aplicar (L.)</th>
                    <th className="p-2 pr-4">Proyecto</th>
                  </tr>
                </thead>
                <tbody>
                  {allocations.map((a, i) => (
                    <tr key={a.factura_id} className={`border-t border-border/40 ${a.vencida ? "bg-red-500/[0.04]" : ""}`} data-testid={`alloc-${a.factura_numero}`}>
                      <td className="p-2 pl-4 font-mono text-xs text-agro-green">
                        {a.factura_numero}
                        {a.subcuenta_codigo && <div className="text-[10px] text-text-muted" data-testid={`alloc-subcuenta-${a.factura_numero}`}>{a.subcuenta_codigo}</div>}
                        {a.requiere_criterios && (
                          <button type="button" onClick={() => setCritAbierto(critAbierto === a.factura_id ? null : a.factura_id)}
                            data-testid={`alloc-criterios-${a.factura_numero}`}
                            className={`mt-1 block text-[10px] font-sans px-1.5 py-0.5 rounded border ${a.criterios?.lineas?.length || a.criterios_guardados
                              ? "border-agro-green/50 text-agro-green" : "border-agro-yellow/60 text-agro-yellow"}`}>
                            {a.criterios?.lineas?.length ? "Criterios capturados" : a.criterios_guardados ? "Criterios guardados" : "Completar criterios GRATIVA"}
                          </button>
                        )}
                      </td>
                      <td className="p-2 text-xs">
                        {a.fecha_vencimiento
                          ? <span className={a.vencida ? "text-red-400 font-medium" : "text-text-secondary"}>{fmtDate(a.fecha_vencimiento)}{a.vencida && <WarningIcon size={11} className="inline align-[-1px] ml-1" aria-label="Vencida" />}</span>
                          : <span className="text-text-muted">—</span>}
                      </td>
                      <td className="p-2 text-xs text-text-secondary max-w-[220px]" data-testid={`alloc-memo-${a.factura_numero}`}>
                        <MemoCelda texto={a.memo} />
                      </td>
                      <td className="p-2 text-xs">
                        <select value={a.tipo_venta} disabled={!canChangeTipo} onChange={(e) => setAllocTipo(i, e.target.value)}
                          data-testid={`alloc-tipo-${a.factura_numero}`}
                          title={canChangeTipo ? "Define si este cobro genera GRATIVA RTC" : "Sin permiso para cambiar el tipo"}
                          className={`bg-app border rounded px-1.5 py-1 text-xs disabled:opacity-70 ${a.tipo_venta === "rtc" ? "border-agro-green/50 text-agro-green" : "border-border"}`}>
                          <option value="directa">Venta directa</option>
                          <option value="rtc">Venta por RTC</option>
                        </select>
                        {a.tipo_venta === "rtc" && a.vendedor && <div className="text-[10px] text-text-muted mt-0.5 truncate max-w-[130px]">{a.vendedor}</div>}
                      </td>
                      <td className="p-2 text-right text-xs text-agro-yellow">{fmtL(a.saldo)}</td>
                      <td className="p-2 text-right">
                        <input type="number" step="0.01" min="0" max={a.saldo} value={a.monto}
                          onChange={(e) => setAllocMonto(i, e.target.value)}
                          data-testid={`alloc-monto-${a.factura_numero}`}
                          className={`w-28 bg-app border rounded px-2 py-1 text-xs text-right focus:border-agro-green ${Number(a.monto) > 0 ? "border-agro-green/50 text-agro-green" : "border-border"}`} />
                      </td>
                      <td className="p-2 pr-4">
                        <select value={a.proyecto_id} onChange={(e) => setAllocProyecto(i, e.target.value)}
                          data-testid={`alloc-proj-${a.factura_numero}`}
                          className="w-full bg-app border border-border rounded px-2 py-1 text-xs">
                          <option value="">— Sin proyecto —</option>
                          {proyectosCliente.map((p) => <option key={p.id} value={p.id}>{p.codigo_subcuenta ? `${p.codigo_subcuenta} · ` : ""}{p.codigo} · {p.nombre}</option>)}
                        </select>
                      </td>
                    </tr>
                  )).flatMap((fila, i) => {
                    const a = allocations[i];
                    if (critAbierto !== a.factura_id) return [fila];
                    return [fila, (
                      <tr key={`crit-${a.factura_id}`} className="bg-app/60">
                        <td colSpan="7" className="p-3">
                          <CriteriosEditor alloc={a} onChange={(c) => setAllocCriterios(i, c)} />
                        </td>
                      </tr>
                    )];
                  })}
                </tbody>
                <tfoot className="bg-sidebar">
                  <tr className="text-xs">
                    <td className="p-2 pl-4 font-semibold" colSpan="5">Suma aplicada</td>
                    <td className="p-2 text-right font-semibold text-agro-green" data-testid="suma-aplicada">{fmtL(sumaAlloc)}</td>
                    <td className="p-2 pr-4"></td>
                  </tr>
                  {excedente > 0.01 && (
                    <tr className="text-xs border-t border-border/40">
                      <td className="p-2 pl-4 text-agro-yellow" colSpan="5">↳ Sobrante (saldo a favor)</td>
                      <td className="p-2 text-right font-semibold text-agro-yellow" data-testid="suma-excedente">{fmtL(excedente)}</td>
                      <td className="p-2 pr-4 text-[10px] text-text-muted">se guarda como anticipo</td>
                    </tr>
                  )}
                  {exceso && (
                    <tr className="text-xs border-t border-border/40 bg-red-500/10">
                      <td className="p-2 pl-4 text-red-400" colSpan="7"><WarningIcon size={12} className="inline align-[-2px]" aria-hidden="true" /> La suma aplicada excede el monto entregado en L. {(sumaAlloc - Number(montoTotal)).toFixed(2)}</td>
                    </tr>
                  )}
                </tfoot>
              </table>
            </div>
          </div>

          <ComisionPreview clienteId={group.cliente_id} fecha={fechaOp.fija ? fechaOp.hoy : fechaPago} allocations={allocations} />

          <div className="flex gap-2">
            {ed && (
              <button type="button" onClick={onClose} data-testid="multi-cancelar"
                className="px-5 py-3 rounded-md border border-border text-sm hover:border-text-muted">Cancelar sin guardar</button>
            )}
            <button type="submit" disabled={(saving || exceso || faltaEfectivo) || mp.saving}
              data-testid="multi-submit"
              className="flex-1 bg-agro-green hover:bg-agro-green-hover text-black font-semibold rounded-md py-3 transition-all active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed">
              {saving ? (ed ? "Guardando…" : "Registrando…") : ed ? `Guardar cambios del recibo · ${fmtL(Number(montoTotal) || 0)}` : `Registrar cobro · ${fmtL(Number(montoTotal) || 0)}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Kpi({ label, v, accent }) {
  const map = { green: "text-agro-green", yellow: "text-agro-yellow", red: "text-red-400", "": "text-text-primary" };
  return (
    <div className="bg-card border border-border/60 rounded-lg p-5">
      <div className="text-[10px] uppercase tracking-wider text-text-muted">{label}</div>
      <div className={`font-heading text-2xl font-semibold mt-1 ${map[accent || ""]}`}>{v}</div>
    </div>
  );
}
const Lbl = ({ children }) => <label className="text-[10px] uppercase tracking-wider text-text-secondary font-medium mb-1.5 block">{children}</label>;

const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
const fmtUSD = (n) => `US$ ${(Number(n) || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

/** Memo / Detalle: abreviado en la celda; al pasar el cursor, comentario emergente con el texto completo.
 *  Se dibuja sobre la página (fixed + portal) para que la tabla o el modal no lo recorten. */
function MemoCelda({ texto }) {
  const [pos, setPos] = useState(null);
  if (!texto) return <span className="text-text-muted">—</span>;
  const abrir = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    const ancho = Math.min(380, window.innerWidth - 24);
    setPos({ left: Math.max(12, Math.min(r.left, window.innerWidth - ancho - 12)), top: r.bottom + 6, ancho, arriba: r.top });
  };
  return (
    <div onMouseEnter={abrir} onMouseLeave={() => setPos(null)} className="cursor-help">
      <div className="truncate" data-testid="memo-texto">{texto}</div>
      {pos && createPortal(
        <div role="tooltip" data-testid="memo-emergente"
          style={{ position: "fixed", left: pos.left, maxWidth: pos.ancho, zIndex: 1000,
            ...(pos.top + 160 > window.innerHeight ? { bottom: window.innerHeight - pos.arriba + 6 } : { top: pos.top }) }}
          className="pointer-events-none bg-card border border-border rounded-md shadow-xl px-3 py-2 text-xs text-text-primary whitespace-pre-wrap break-words">
          {texto}
        </div>, document.body)}
    </div>
  );
}


/** Criterios GRATIVA de una factura histórica: RTC beneficiario y desglose del importe aplicado por
 *  proveedor / categoría / nivel. La suma debe coincidir con el pago aplicado a la factura. */
function CriteriosEditor({ alloc, onChange }) {
  const [reps, setReps] = useState([]);
  const [cats, setCats] = useState(["Semillas", "Producto", "Promociones"]);
  const [sups, setSups] = useState([]);
  useEffect(() => {
    api.get("/representatives").then(({ data }) => setReps(data || [])).catch(() => {});
    api.get("/commissions/bonification-table").then(({ data }) => {
      const c = [...new Set((data || []).filter((r) => r.activa !== false).map((r) => r.categoria).filter(Boolean))];
      if (c.length) setCats(c);
    }).catch(() => {});
    api.get("/suppliers").then(({ data }) => setSups((data || []).map((x) => x.nombre).filter(Boolean))).catch(() => {});
  }, []);
  const g = alloc.criterios_guardados || {};
  const c = alloc.criterios || {
    rtc_id: g.rtc_id || "",
    lineas: (g.lineas || []).length
      ? g.lineas.map((l) => ({ proveedor_nombre: l.proveedor_nombre, categoria: l.categoria, nivel: l.nivel,
          monto: Math.round(Number(alloc.monto || 0) * (Number(l.porcentaje) || 0)) / 100 }))
      : [{ proveedor_nombre: "", categoria: "", nivel: "", monto: Number(alloc.monto) || 0 }],
  };
  const set = (k, v) => onChange({ ...c, [k]: v });
  const setLinea = (i, k, v) => set("lineas", c.lineas.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const suma = Math.round(c.lineas.reduce((t, l) => t + (Number(l.monto) || 0), 0) * 100) / 100;
  const cuadra = Math.abs(suma - Number(alloc.monto || 0)) <= 0.01;
  return (
    <div className="space-y-2 text-xs" data-testid={`criterios-editor-${alloc.factura_numero}`}>
      <div className="text-text-secondary">
        Factura histórica <b className="font-mono">{alloc.factura_numero}</b>: indica el RTC y cómo se distribuye el pago aplicado
        ({fmtL(Number(alloc.monto) || 0)}) por proveedor, categoría y nivel. Se guarda para los siguientes cobros.
        {g.actualizado_por && <span className="text-text-muted"> Última captura: {g.actualizado_por}.</span>}
      </div>
      <div className="flex items-center gap-2">
        <span className="text-text-muted">RTC beneficiario</span>
        <select value={c.rtc_id || ""} onChange={(e) => set("rtc_id", e.target.value)} data-testid="crit-rtc"
          className="bg-app border border-border rounded px-2 py-1">
          <option value="">— Venta directa (sin GRATIVA) —</option>
          {reps.map((r) => <option key={r.id} value={r.id}>{r.codigo ? `${r.codigo} · ` : ""}{r.nombre}</option>)}
        </select>
      </div>
      <datalist id="crit-proveedores">{sups.map((n) => <option key={n} value={n} />)}</datalist>
      {c.lineas.map((l, i) => (
        <div key={i} className="grid grid-cols-12 gap-2 items-center">
          <input list="crit-proveedores" value={l.proveedor_nombre} onChange={(e) => setLinea(i, "proveedor_nombre", e.target.value)}
            placeholder="Proveedor" data-testid={`crit-prov-${i}`} className="col-span-4 bg-app border border-border rounded px-2 py-1" />
          <select value={l.categoria} onChange={(e) => setLinea(i, "categoria", e.target.value)} data-testid={`crit-cat-${i}`}
            className="col-span-3 bg-app border border-border rounded px-2 py-1">
            <option value="">Categoría</option>
            {cats.map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <select value={l.nivel} onChange={(e) => setLinea(i, "nivel", e.target.value)} data-testid={`crit-nivel-${i}`}
            className="col-span-2 bg-app border border-border rounded px-2 py-1">
            <option value="">Nivel</option>
            {["P0", "P1", "P2", "P3", "P4"].map((x) => <option key={x} value={x}>{x}</option>)}
          </select>
          <input type="number" step="0.01" value={l.monto} onChange={(e) => setLinea(i, "monto", e.target.value)} data-testid={`crit-monto-${i}`}
            className="col-span-2 bg-app border border-border rounded px-2 py-1 text-right" />
          <button type="button" onClick={() => set("lineas", c.lineas.filter((_, j) => j !== i))} disabled={c.lineas.length === 1}
            className="col-span-1 text-text-muted hover:text-red-400 disabled:opacity-30 p-1.5" aria-label="Quitar forma de pago"><XIcon size={14} /></button>
        </div>
      ))}
      <div className="flex items-center justify-between">
        <button type="button" onClick={() => set("lineas", [...c.lineas, { proveedor_nombre: "", categoria: "", nivel: "", monto: 0 }])}
          className="text-agro-green" data-testid="crit-agregar">+ Agregar proveedor / nivel</button>
        <span className={cuadra ? "text-agro-green" : "text-red-400"} data-testid="crit-suma">
          Desglose {fmtL(suma)} de {fmtL(Number(alloc.monto) || 0)} {cuadra ? <CheckIcon size={11} weight="bold" className="inline align-[-1px]" aria-label="Cuadra" /> : "· debe coincidir"}
        </span>
      </div>
    </div>
  );
}


/** Vista previa del Cálculo GRATIVA del cobro (no se guarda nada): base, deducción por atraso, ajustada, total y pendientes. */
function ComisionPreview({ clienteId, fecha, allocations }) {
  const [data, setData] = useState(null);
  const clave = JSON.stringify(allocations.filter((a) => Number(a.monto) > 0 && (a.documento_tipo || "factura") === "factura")
    .map((a) => [a.factura_id, Number(a.monto), a.tipo_venta, a.criterios]));
  useEffect(() => {
    const allocs = JSON.parse(clave);
    if (!allocs.length) { setData(null); return; }
    const t = setTimeout(() => {
      api.post("/payments/commission-preview", {
        cliente_id: clienteId, fecha,
        allocations: allocs.map(([factura_id, monto, tipo_venta, crit]) => ({
          factura_id, monto, tipo_venta, rtc_id: crit?.rtc_id || null,
          criterios_lineas: (crit?.lineas || []).map((l) => ({ ...l, monto: Number(l.monto) || 0 })),
        })),
      }).then(({ data: d }) => setData(d)).catch(() => setData(null));
    }, 500);
    return () => clearTimeout(t);
  }, [clave, clienteId, fecha]);
  if (!data || !data.facturas.length) return null;
  const aplican = data.facturas.filter((f) => f.aplica);
  const pend = data.facturas.flatMap((f) => (f.pendientes || []).map((p) => ({ ...p, numero: f.factura_numero })));
  if (!aplican.length && !pend.length) return null;
  return (
    <div className="border border-border/60 rounded-md p-3 text-xs space-y-2" data-testid="comision-preview">
      <div className="flex items-center justify-between">
        <span className="text-[10px] uppercase tracking-wider text-text-muted">Cálculo GRATIVA de este cobro (Gratificación Variable · interno · no se imprime en el recibo)</span>
        {data.montos_visibles && <span className={data.definitivo ? "text-agro-green" : "text-agro-yellow"}>{data.definitivo ? "Cálculo definitivo" : "Cálculo con pendientes"}</span>}
      </div>
      {data.montos_visibles && aplican.length > 0 && (
        <table className="w-full">
          <thead className="text-[10px] uppercase text-text-muted"><tr className="text-left">
            <th className="py-1">Factura</th><th>Atraso</th><th className="text-right">GRATIVA base</th>
            <th className="text-right">Deducción por atraso</th><th className="text-right">GRATIVA ajustada</th>
          </tr></thead>
          <tbody>
            {aplican.map((f) => (
              <tr key={f.factura_id} className="border-t border-border/30" data-testid={`prev-${f.factura_numero}`}>
                <td className="py-1 font-mono">{f.factura_numero}{f.rtc ? <span className="font-sans text-text-muted"> · {f.rtc.nombre}</span> : ""}
                  {(f.lineas || []).map((l, k) => (
                    <div key={k} className="font-sans text-[10px] text-text-muted">{l.proveedor} · {l.categoria} {l.nivel} · {l.porcentaje}%{l.modo === "margen" ? " del margen" : ""} sobre {fmtL(l.monto_aplicado)}</div>
                  ))}
                </td>
                <td>{f.dias_atraso != null ? `${f.dias_atraso < 0 ? "Contado" : `${f.dias_atraso} días`} · ${f.tramo} (×${f.factor})` : "—"}</td>
                <td className="text-right">{fmtL(f.base || 0)}</td>
                <td className="text-right text-red-400">{f.deduccion ? `− ${fmtL(f.deduccion)}` : "—"}</td>
                <td className="text-right font-semibold text-agro-green">{fmtL(f.ajustada || 0)}</td>
              </tr>
            ))}
          </tbody>
          <tfoot><tr className="border-t border-border font-semibold">
            <td className="py-1" colSpan="2">Total GRATIVA a pagar</td>
            <td className="text-right">{fmtL(data.totales.base)}</td>
            <td className="text-right text-red-400">− {fmtL(data.totales.deduccion)}</td>
            <td className="text-right text-agro-green" data-testid="prev-total">{fmtL(data.totales.ajustada)}</td>
          </tr></tfoot>
        </table>
      )}
      {pend.length > 0 && (
        <div className="text-agro-yellow space-y-0.5" data-testid="prev-pendientes">
          {pend.map((p, k) => <div key={k}><WarningIcon size={12} className="inline align-[-2px]" aria-hidden="true" /> {p.numero}: {p.detalle}</div>)}
          <div className="text-text-muted">El cobro se registra; la GRATIVA queda pendiente hasta completar ese dato (no se calcula con supuestos).</div>
        </div>
      )}
    </div>
  );
}
